import DeleteIcon from "@/assets/colorable/delete.svg?react";
import PenEdit from "@/assets/colorable/pen_edit.svg?react";
import Button from "@/components/Common/Button";
import {usePayment} from "@/hooks/usePayment";
import StripeCardElement from "@/pages/profile/StripeCardElement";
import {Can} from "@/permissions";
import {CARD_BRANDS} from "@/store/constants";
import {editedPaymentMethodIdAtom} from "@/store/jotaiAtoms";
import type {TPaymentMethodType} from "@/store/types";
import {Card} from "@stripe/stripe-js";
import {useAtom} from "jotai";
import {type FC, memo, useMemo} from "react";
import {useTranslation} from "react-i18next";

const CardComponent: FC<{
  card?: Card;
  isDeletable?: boolean;
  isSelected?: boolean;
  isSelectLoading?: boolean;
  onSelect?: () => void;
  paymentMethodId: string;
  showButtons?: boolean;
  type: TPaymentMethodType;
}> = ({
  card,
  isDeletable = false,
  isSelected = false,
  isSelectLoading = false,
  onSelect,
  paymentMethodId,
  showButtons = false,
  type,
}) => {
  const {t} = useTranslation();
  const [editedPaymentMethodId, setEditedPaymentMethodId] = useAtom(editedPaymentMethodIdAtom);
  const cardBrand = useMemo(
    () => CARD_BRANDS[type === "card" && !!card ? card.brand.trim().toLowerCase() : type],
    [card?.brand, type],
  );
  const isCardFormOpen = useMemo(
    () => paymentMethodId === editedPaymentMethodId,
    [paymentMethodId, editedPaymentMethodId],
  );
  const {detachPaymentMethod, detachLoading} = usePayment();

  return isCardFormOpen ? (
    <StripeCardElement paymentMethodId={paymentMethodId} />
  ) : (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:gap-0">
        <div className="flex items-center gap-2.5">
          {showButtons && !!onSelect && (
            <input
              type="radio"
              checked={isSelected}
              disabled={isSelectLoading}
              onChange={onSelect}
              className="disabled:accent-neutral-grey-dark accent-primary-dark-solid disabled:cursor-not-allowed"
            />
          )}
          <div className="rounded-full border-1 px-3 py-1">
            <img src={cardBrand} className="h-[18px]" />
          </div>
          {type === "card" && <span className="text-sm font-medium">**** {card?.last4}</span>}
        </div>
        {showButtons && (
          <div className="flex items-center gap-3">
            {type === "card" && (
              <Can I="update" a="CompanyPaymentMethod">
                <Button
                  onClick={() => setEditedPaymentMethodId(paymentMethodId)}
                  buttonStyle="White"
                  isDisabled={detachLoading}
                  className="h-auto items-center gap-2.5 px-4 py-1">
                  <PenEdit className="size-3.5 shrink-0" />
                  {t("editCardInfo")}
                </Button>
              </Can>
            )}
            {isDeletable && (
              <Can I="delete" a="CompanyPaymentMethod">
                <Button
                  onClick={() => detachPaymentMethod({paymentMethodId})}
                  buttonStyle="White"
                  isDisabled={detachLoading}
                  className="size-8">
                  <DeleteIcon className="size-4 shrink-0" />
                </Button>
              </Can>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CardComponent);
